import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import SeoPageComponent from '../../components/SeoPageComponent/SeoPageComponent';
import '../DirectAwards/DirectAwards.css';
import { fetchPINoticeSearchResults } from '../../services/publicTendersApi';
import AuctionList from '../../components/AuctionList/AuctionList';
import logo from '../../assets/icon.png';
import Breadcrumbs from '../../components/Breadcrumbs';
import ListLoadingHolder from '../../components/LoadingHolders/ListLoadingHolder';
import directAwardCounties from '../DirectAwards/directAwardCounties';
import piFilterLabels from './piFilterLabels';

const PriorInformation = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialFilters = () => ({
    auctionId: searchParams.get('auctionId') || '',
    auctionTitle: searchParams.get('auctionTitle') || '',
    publishedDate: searchParams.get('publishedDate') || '',
    cpvCode: searchParams.get('cpvCode') || '',
    authorityName: searchParams.get('authorityName') || '',
    authorityCUI: searchParams.get('authorityCUI') || '',
    city: searchParams.get('city') || '',
    county: searchParams.get('county') || '',
    minEstimatedValue: searchParams.get('minEstimatedValue') || '',
    maxEstimatedValue: searchParams.get('maxEstimatedValue') || '',
    currency: searchParams.get('currency') || '',
    contractType: searchParams.get('contractType') || '',
    pageIndex: parseInt(searchParams.get('pageIndex') || '0'),
    pageSize: parseInt(searchParams.get('pageSize') || '10'),
  });

  const [filters, setFilters] = useState(getInitialFilters());
  const [errors, setErrors] = useState([]);
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    const params = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined && value !== 0) {
        if ((key === 'pageIndex' && value === 0) || (key === 'pageSize' && value === 10)) return;
        params[key] = value.toString();
      }
    });
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const validateValues = useCallback(() => {
    const validationErrors = [];
    if (parseFloat(filters.minEstimatedValue) < 0)
      validationErrors.push('Valoarea estimată minimă nu poate fi negativă.');
    if (parseFloat(filters.maxEstimatedValue) < 0)
      validationErrors.push('Valoarea estimată maximă nu poate fi negativă.');
    if (parseFloat(filters.maxEstimatedValue) < parseFloat(filters.minEstimatedValue))
      validationErrors.push('Valoarea estimată minimă nu poate fi mai mare decât valoarea maximă.');
    setErrors(validationErrors);
    return validationErrors.length === 0;
  }, [filters]);

  // Auto-fetch when filters change
  useEffect(() => {
    let cancelled = false;
    if (!validateValues()) return;
    setLoading(true);
    fetchPINoticeSearchResults(filters)
      .then((data) => {
        if (cancelled) return;
        setResults(data.results);
        setTotalResults(data.totalResults);
      })
      .catch((error) => {
        if (!cancelled) console.error('Error fetching PI notices:', error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilter = useCallback(() => {
    // no-op: filter changes drive fetches via the effect above
  }, []);
  useEffect(() => { if (window.prerenderReady !== undefined) window.prerenderReady = true; }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value, pageIndex: 0 }));
  };

  const handleReset = () => {
    setFilters({
      auctionId: '', auctionTitle: '', publishedDate: '', cpvCode: '',
      authorityName: '', authorityCUI: '', city: '', county: '',
      minEstimatedValue: '', maxEstimatedValue: '', currency: '', contractType: '',
      pageIndex: 0, pageSize: 10,
    });
    setErrors([]);
    setResults([]);
    setTotalResults(0);
    handleFilter();
  };

  const breadcrumbLinks = [
    { href: '/', label: 'Home' },
    { label: 'Anunțuri de Intenție' },
  ];

  const pageTitle = 'Anunțuri de Intenție - Licitatii-Constructii';
  const pageDescription = 'Descoperă cele mai recente anunțuri de intenție pentru proiecte de construcții, lucrări și servicii.';
  const pageUrl = `${import.meta.env.VITE_DOMAIN}/anunturi-intentie`;

  const canonicalUrl = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'pageIndex') return;
      if (key === 'pageSize' && value === 10) return;
      if (value !== '' && value !== null && value !== undefined && value !== 0)
        params.append(key, value.toString());
    });
    const queryString = params.toString();
    return `${window.location.origin}/anunturi-intentie${queryString ? '?' + queryString : ''}`;
  }, [filters]);

  return (
    <>
      <SeoPageComponent pageTitle={pageTitle} pageDescription={pageDescription} pageUrl={pageUrl} canonicalUrl={canonicalUrl} logoUrl={logo} />
      <section className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-green-100 py-12">
        <div className="container max-w-5xl mx-auto">
          <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />
          <h1 className="text-4xl md:text-5xl font-extrabold text-indigo-700 mb-4 text-center">
            Anunțuri de Intenție - Construcții, Lucrări, Servicii
          </h1>

          <div className="bg-white rounded-2xl shadow-2xl p-8 border-t-8 border-indigo-500 mb-10 animate-fade-in">
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="w-full bg-gradient-to-r from-indigo-500 to-green-500 text-white px-4 py-3 rounded-xl font-bold text-lg hover:from-indigo-600 hover:to-green-600 transition-all flex justify-between items-center mb-4"
            >
              {isFilterOpen ? 'Ascunde filtrele ▲' : 'Afișează filtrele ▼'}
            </button>
            {isFilterOpen && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
                {Object.keys(filters)
                  .filter((f) => f !== 'pageIndex' && f !== 'pageSize')
                  .map((filter) =>
                    filter === 'county' ? (
                      <div key={filter}>
                        <label className="block text-gray-700 font-semibold mb-1">
                          {piFilterLabels[filter]?.label || filter}
                        </label>
                        <select name={filter} value={filters[filter]} onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none">
                          <option value="">{piFilterLabels[filter]?.placeholder || 'Selectează'}</option>
                          {directAwardCounties.map((c) => (
                            <option key={c.value} value={c.value}>{c.text}</option>
                          ))}
                        </select>
                      </div>
                    ) : filter === 'contractType' ? (
                      <div key={filter}>
                        <label className="block text-gray-700 font-semibold mb-1">
                          {piFilterLabels[filter]?.label || filter}
                        </label>
                        <select name={filter} value={filters[filter]} onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none">
                          <option value="">Toate</option>
                          <option value="Lucrari">Lucrări</option>
                          <option value="Servicii">Servicii</option>
                          <option value="Furnizare">Furnizare</option>
                        </select>
                      </div>
                    ) : (
                      <div key={filter}>
                        <label className="block text-gray-700 font-semibold mb-1">
                          {piFilterLabels[filter]?.label || filter}
                        </label>
                        <input
                          type={filter.includes('Date') ? 'date' : filter.includes('Value') ? 'number' : 'text'}
                          name={filter} value={filters[filter]} onChange={handleInputChange}
                          placeholder={piFilterLabels[filter]?.placeholder || ''}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                        />
                      </div>
                    )
                  )}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 animate-fade-in">
            <div className="flex items-center justify-end space-x-4 border-b pb-5 mb-8">
              <button onClick={() => handleFilter()}
                className="bg-gradient-to-r from-indigo-500 to-green-500 text-white px-6 py-2 rounded-xl font-bold hover:from-indigo-600 hover:to-green-600 transition-all">
                Filtrează
              </button>
              <button onClick={handleReset}
                className="bg-red-100 hover:bg-red-300 text-gray-700 px-6 py-2 rounded-xl font-bold hover:text-white duration-300">
                Resetează
              </button>
            </div>

            <div className="mt-8">
              {loading ? (
                <ListLoadingHolder />
              ) : (
                <AuctionList auctions={results} totalResults={totalResults}
                  pageSize={filters.pageSize} pageIndex={filters.pageIndex} auctionType={'PriorInformation'} />
              )}
            </div>

            {!loading && totalResults > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-2 mt-10">
                <button onClick={() => setFilters((p) => ({ ...p, pageIndex: 0 }))} disabled={filters.pageIndex === 0}
                  className="px-3 py-2 rounded-lg font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition disabled:opacity-50">Prima</button>
                <button onClick={() => setFilters((p) => ({ ...p, pageIndex: p.pageIndex - 1 }))} disabled={filters.pageIndex === 0}
                  className="px-3 py-2 rounded-lg font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition disabled:opacity-50">&laquo;</button>
                {Array.from({ length: Math.min(5, Math.ceil(totalResults / filters.pageSize)) }, (_, i) => {
                  let startPage = Math.max(0, Math.min(filters.pageIndex - 2, Math.ceil(totalResults / filters.pageSize) - 5));
                  let page = startPage + i;
                  if (page < 0 || page >= Math.ceil(totalResults / filters.pageSize)) return null;
                  return (
                    <button key={page} onClick={() => setFilters((p) => ({ ...p, pageIndex: page }))}
                      className={`px-3 py-2 rounded-lg font-bold ${filters.pageIndex === page ? 'bg-indigo-500 text-white' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'} transition`}>
                      {page + 1}
                    </button>
                  );
                })}
                <button onClick={() => setFilters((p) => ({ ...p, pageIndex: p.pageIndex + 1 }))}
                  disabled={filters.pageIndex >= Math.ceil(totalResults / filters.pageSize) - 1}
                  className="px-3 py-2 rounded-lg font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition disabled:opacity-50">&raquo;</button>
                <button onClick={() => setFilters((p) => ({ ...p, pageIndex: Math.max(0, Math.ceil(totalResults / filters.pageSize) - 1) }))}
                  disabled={filters.pageIndex >= Math.ceil(totalResults / filters.pageSize) - 1}
                  className="px-3 py-2 rounded-lg font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition disabled:opacity-50">Ultima</button>
                <label className="ml-4 text-gray-700 text-sm flex items-center gap-2">
                  / pagină
                  <select value={filters.pageSize} onChange={(e) => setFilters((p) => ({ ...p, pageSize: Number(e.target.value), pageIndex: 0 }))}
                    className="ml-1 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-400 outline-none bg-indigo-50">
                    {[10, 20, 50, 100].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <span className="ml-4 text-gray-500 text-sm">
                  Pagina {Math.min(filters.pageIndex + 1, Math.max(1, Math.ceil(totalResults / filters.pageSize)))} din {Math.max(1, Math.ceil(totalResults / filters.pageSize))}
                </span>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default PriorInformation;
